import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Rocket, Heart, Users, Zap, Globe, TrendingUp } from "lucide-react";

const Careers = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Careers at HiStage",
    "description": "Join our team and help revolutionize live streaming. Explore open positions and grow your career at HiStage.",
    "url": "https://histage.lovable.app/careers"
  };

  const openings = [
    {
      title: "Senior Frontend Engineer",
      department: "Engineering",
      location: "Remote",
      type: "Full-time"
    },
    {
      title: "Product Designer",
      department: "Design",
      location: "Remote",
      type: "Full-time"
    },
    {
      title: "Creator Success Manager",
      department: "Community",
      location: "Remote",
      type: "Full-time"
    },
    {
      title: "Marketing Lead",
      department: "Marketing",
      location: "Remote",
      type: "Full-time"
    }
  ];

  const benefits = [
    { icon: Rocket, title: "Fast Growth", desc: "Shape the future of live streaming" },
    { icon: Heart, title: "Great Benefits", desc: "Health, dental, vision, and 401k" },
    { icon: Users, title: "Amazing Team", desc: "Work with passionate, talented people" },
    { icon: Zap, title: "Ownership", desc: "Competitive equity packages" },
    { icon: Globe, title: "Remote First", desc: "Work from anywhere" },
    { icon: TrendingUp, title: "Learning Budget", desc: "Continuous growth opportunities" }
  ];

  return (
    <>
      <SEO 
        title="Careers at HiStage - Join Our Team"
        description="Join HiStage and help revolutionize live streaming. We're hiring talented individuals passionate about connecting creators and fans. Explore remote opportunities in engineering, design, marketing, and more."
        url="/careers"
        keywords={[
          "histage careers",
          "live streaming jobs",
          "remote tech jobs",
          "startup careers",
          "engineering jobs",
          "product design jobs"
        ]}
        schema={schema}
      />
      
      <main className="min-h-screen bg-background">
        {/* Hero */}
        <section className="py-20 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl font-bold text-foreground mb-6">
                Build the Future of Live Streaming
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Join our remote-first team and help creators connect with millions of fans worldwide
              </p>
              <Button size="lg" className="rounded-full px-8" asChild>
                <a href="#openings">View Open Positions</a>
              </Button>
            </div>
          </div>
        </section>

        {/* Why Join */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-foreground mb-4">
                  Why Join HiStage?
                </h2>
                <p className="text-xl text-muted-foreground">
                  Perks and benefits of working with us
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {benefits.map((benefit, i) => (
                  <div key={i} className="clean-card rounded-2xl p-6">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                      <benefit.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">{benefit.title}</h3>
                    <p className="text-sm text-muted-foreground">{benefit.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Open Positions */}
        <section id="openings" className="py-20 bg-gradient-to-b from-background to-primary/5">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-foreground mb-4">
                  Open Positions
                </h2>
                <p className="text-xl text-muted-foreground">
                  Find your next role at HiStage
                </p>
              </div>

              <div className="space-y-4">
                {openings.map((job, i) => (
                  <div key={i} className="clean-card rounded-2xl p-6 hover-lift cursor-pointer">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div>
                        <h3 className="text-xl font-bold text-foreground mb-2">{job.title}</h3>
                        <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                          <span>{job.department}</span>
                          <span>•</span>
                          <span>{job.location}</span>
                          <span>•</span>
                          <span>{job.type}</span>
                        </div>
                      </div>
                      <Button className="rounded-full px-6">
                        Apply Now
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-12 text-center">
                <p className="text-muted-foreground mb-6">
                  Don't see a role that fits? We're always looking for talented people.
                </p>
                <Link to="/contact">
                  <Button variant="outline" className="rounded-full px-8">
                    Get in Touch
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Careers;
